'use client';

import { useState, useRef } from 'react';
import Link from 'next/link';
import { motion, AnimatePresence } from 'framer-motion';
import {
    Phone, Menu, X,
    Calendar, CreditCard, Users, Zap, Globe, BarChart3,
    Coffee, Briefcase, Building2, HeartPulse, GraduationCap, ShoppingBag, Scissors,
    ChevronDown, ArrowRight,
} from 'lucide-react';
import { EASE_OUT, EASE_CUSTOM } from '@/lib/animations'
import Logo from './ui/Logo';

/* ── Social icons (inline SVG for bundle size) ── */
interface SocialIconProps {
    href: string;
    label: string;
    children: React.ReactNode;
    className?: string;
    [key: string]: any;
}

const SocialIcon = ({ href, label, children, className = '' }: SocialIconProps) => (
    <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={label}
        className={`w-7 h-7 rounded-lg flex items-center justify-center text-slate-400 hover:text-blue-600 hover:bg-blue-50/60 transition-all duration-200 ${className}`}
    >
        {children}
    </a>
);

const InstagramIcon = () => (
    <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24">
        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" />
    </svg>
);

const TikTokIcon = () => (
    <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24">
        <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.1V9.01a6.27 6.27 0 00-.79-.05 6.34 6.34 0 00-6.34 6.34 6.34 6.34 0 006.34 6.34 6.34 6.34 0 006.34-6.34V8.75a8.18 8.18 0 004.76 1.52V6.84a4.84 4.84 0 01-1-.15z" />
    </svg>
);

const TwitterXIcon = () => (
    <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24">
        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
    </svg>
);

const SOCIAL_LINKS = [
    { href: 'https://www.instagram.com/innovathera.tech', label: 'Instagram', icon: InstagramIcon },
    { href: 'https://www.tiktok.com/@innovathera.tech', label: 'TikTok', icon: TikTokIcon },
    { href: 'https://x.com/innovathera', label: 'Twitter / X', icon: TwitterXIcon },
];

const SOLUTIONS = [
    { label: 'Sistema de Reservas', slug: 'reservas', icon: Calendar, desc: 'Agenda online 24/7 con pagos' },
    { label: 'Pasarelas de Pago', slug: 'pagos', icon: CreditCard, desc: 'Stripe, Bizum y facturación auto' },
    { label: 'CRM a Medida', slug: 'crm', icon: Users, desc: 'Gestión de clientes centralizada' },
    { label: 'Automatizaciones', slug: 'automatizaciones', icon: Zap, desc: 'Email, WhatsApp y tareas auto' },
    { label: 'Web de Captación', slug: 'web', icon: Globe, desc: 'Landing pages que captan y convierten' },
    { label: 'Dashboard & Analytics', slug: 'dashboard', icon: BarChart3, desc: 'Métricas en tiempo real' },
];

const SECTORS = [
    { label: 'Hostelería', slug: 'hosteleria', icon: Coffee },
    { label: 'Gestoría', slug: 'gestoria', icon: Briefcase },
    { label: 'Construcción', slug: 'construccion', icon: Building2 },
    { label: 'Clínicas', slug: 'clinicas', icon: HeartPulse },
    { label: 'Formación', slug: 'formacion', icon: GraduationCap },
    { label: 'E-commerce', slug: 'ecommerce', icon: ShoppingBag },
    { label: 'Peluquería', slug: 'peluqueria', icon: Scissors },
];

function MegaMenu({ onClose }: { onClose: () => void }) {
    return (
        <motion.div
            initial={{ opacity: 0, y: -8, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -8, scale: 0.98 }}
            transition={{ duration: 0.2, ease: EASE_OUT }}
            className="absolute left-1/2 top-full mt-3 -translate-x-1/2 w-[720px] rounded-2xl border border-white/60 bg-white/80 backdrop-blur-2xl shadow-[0_20px_60px_rgba(15,23,42,0.12)] overflow-hidden"
        >
            <div className="grid grid-cols-2 gap-0">
                {/* Soluciones */}
                <div className="p-6 border-r border-slate-100/80">
                    <p className="text-[10px] font-mono tracking-[0.25em] text-slate-400 uppercase mb-4">Soluciones</p>
                    <div className="flex flex-col gap-1">
                        {SOLUTIONS.map(sol => {
                            const Icon = sol.icon;
                            return (
                                <Link
                                    key={sol.slug}
                                    href={`/soluciones/${sol.slug}`}
                                    onClick={onClose}
                                    className="group flex items-center gap-3 rounded-xl px-3 py-2.5 hover:bg-blue-50/70 transition-all duration-150"
                                >
                                    <div className="w-8 h-8 rounded-lg bg-blue-100/60 border border-blue-100 flex items-center justify-center shrink-0 group-hover:bg-blue-100 transition-colors">
                                        <Icon className="w-4 h-4 text-blue-600" />
                                    </div>
                                    <div className="min-w-0">
                                        <p className="text-[13px] font-semibold text-slate-800 group-hover:text-blue-700 transition-colors leading-tight">{sol.label}</p>
                                        <p className="text-[11px] text-slate-400 leading-tight mt-0.5">{sol.desc}</p>
                                    </div>
                                    <ArrowRight className="w-3.5 h-3.5 text-slate-300 group-hover:text-blue-400 ml-auto shrink-0 opacity-0 group-hover:opacity-100 transition-all translate-x-[-4px] group-hover:translate-x-0" />
                                </Link>
                            );
                        })}
                    </div>
                </div>

                {/* Por Sector */}
                <div className="p-6">
                    <p className="text-[10px] font-mono tracking-[0.25em] text-slate-400 uppercase mb-4">Por Sector</p>
                    <div className="flex flex-col gap-1">
                        {SECTORS.map(sec => {
                            const Icon = sec.icon;
                            return (
                                <Link
                                    key={sec.slug}
                                    href={`/sectores/${sec.slug}`}
                                    onClick={onClose}
                                    className="group flex items-center gap-3 rounded-xl px-3 py-2 hover:bg-slate-50/80 transition-all duration-150"
                                >
                                    <div className="w-7 h-7 rounded-lg bg-slate-100/80 border border-slate-100 flex items-center justify-center shrink-0 group-hover:bg-slate-100 transition-colors">
                                        <Icon className="w-3.5 h-3.5 text-slate-500" />
                                    </div>
                                    <p className="text-[13px] font-medium text-slate-700 group-hover:text-slate-900 transition-colors">{sec.label}</p>
                                    <ArrowRight className="w-3 h-3 text-slate-300 group-hover:text-slate-400 ml-auto shrink-0 opacity-0 group-hover:opacity-100 transition-all translate-x-[-4px] group-hover:translate-x-0" />
                                </Link>
                            );
                        })}
                    </div>

                    {/* Bottom CTA */}
                    <div className="mt-4 pt-4 border-t border-slate-100/80">
                        <Link
                            href="/contacto"
                            onClick={onClose}
                            className="flex items-center justify-between rounded-xl bg-blue-600 px-4 py-3 text-white transition-all hover:bg-blue-700 group"
                        >
                            <span className="text-[13px] font-semibold">Diagnóstico gratuito</span>
                            <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
                        </Link>
                    </div>
                </div>
            </div>
        </motion.div>
    );
}

export default function Navbar() {
    const [mobileOpen, setMobileOpen] = useState(false);
    const [megaOpen, setMegaOpen] = useState(false);
    const [mobileProductoOpen, setMobileProductoOpen] = useState(false);
    const megaTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
    const close = () => { setMobileOpen(false); setMobileProductoOpen(false); };

    const openMega = () => {
        if (megaTimeout.current) clearTimeout(megaTimeout.current);
        setMegaOpen(true);
    };
    const closeMega = () => {
        megaTimeout.current = setTimeout(() => setMegaOpen(false), 120);
    };

    return (
        <nav className="fixed left-0 right-0 top-3 z-50 px-3">
            <div className="pointer-events-auto w-full flex h-[60px] items-center justify-between rounded-full border border-white/55 bg-white/45 px-5 shadow-[0_4px_24px_rgba(15,23,42,0.06)] backdrop-blur-xl md:grid md:grid-cols-3 lg:px-8">

                {/* Logo */}
                <Link href="/" aria-label="Innovathera — Ir a inicio" onClick={() => { close(); window.scrollTo({ top: 0, behavior: 'smooth' }); }} className="flex items-center gap-2.5 group w-fit">
                    <Logo height={26} className="text-blue-600 group-hover:text-blue-700 transition-colors" />
                </Link>

                {/* Desktop center links */}
                <div className="hidden md:flex items-center justify-center gap-6">
                    {/* Producto megamenu trigger */}
                    <div
                        className="relative"
                        onMouseEnter={openMega}
                        onMouseLeave={closeMega}
                    >
                        <button className="flex items-center gap-1 text-[13px] font-medium text-slate-600 hover:text-slate-900 transition-colors duration-200">
                            Producto
                            <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${megaOpen ? 'rotate-180' : ''}`} />
                        </button>
                        <AnimatePresence>
                            {megaOpen && <MegaMenu onClose={() => { setMegaOpen(false); setMobileOpen(false); }} />}
                        </AnimatePresence>
                    </div>

                    <Link href="/software-ia" className="text-[13px] font-medium text-slate-600 hover:text-slate-900 transition-colors duration-200">
                        IA & Automatización
                    </Link>
                    <Link href="/#proceso" className="text-[13px] font-medium text-slate-600 hover:text-slate-900 transition-colors duration-200">
                        Proceso
                    </Link>
                    <Link href="/#nosotros" className="text-[13px] font-medium text-slate-600 hover:text-slate-900 transition-colors duration-200">
                        Sobre nosotros
                    </Link>
                    <Link href="/#precios" className="text-[13px] font-medium text-slate-600 hover:text-slate-900 transition-colors duration-200">
                        Precios
                    </Link>
                    <Link href="/contacto" className="text-[13px] font-medium text-slate-600 hover:text-slate-900 transition-colors duration-200">
                        Contacto
                    </Link>
                </div>

                {/* Desktop CTA + RRSS + Mobile hamburger */}
                <div className="flex items-center justify-end gap-3">
                    {/* RRSS icons — desktop only */}
                    <div className="hidden md:flex items-center gap-0.5 mr-1">
                        {SOCIAL_LINKS.map(({ href, label, icon: Icon }) => (
                            <SocialIcon key={label} href={href} label={label}>
                                <Icon />
                            </SocialIcon>
                        ))}
                    </div>
                    <Link
                        href="/#contacto"
                        className="hidden md:flex items-center gap-2 rounded-xl bg-blue-600 px-5 py-2 text-[13px] font-bold tracking-wide text-white shadow-[0_4px_16px_rgba(37,99,235,0.3)] transition-all duration-150 hover:bg-blue-700 active:scale-95"
                    >
                        <Phone className="w-3.5 h-3.5" />
                        Quiero un presupuesto
                    </Link>
                    <button
                        className="md:hidden flex items-center justify-center h-10 w-10 rounded-xl border border-slate-200/70 bg-white/70 text-slate-700"
                        onClick={() => setMobileOpen(o => !o)}
                        aria-label="Toggle menu"
                    >
                        {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                    </button>
                </div>
            </div>

            {/* Mobile dropdown */}
            <AnimatePresence>
                {mobileOpen && (
                    <motion.div
                        initial={{ opacity: 0, y: -8 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -8 }}
                        transition={{ duration: 0.2 }}
                        className="md:hidden mt-2 mx-auto max-w-7xl rounded-2xl border border-white/70 bg-white/85 backdrop-blur-xl px-4 py-4 flex flex-col gap-0.5 shadow-[0_8px_32px_rgba(15,23,42,0.1)]"
                    >
                        {/* Producto — accordion toggle */}
                        <button
                            onClick={() => setMobileProductoOpen(o => !o)}
                            className="flex items-center justify-between w-full px-3 py-3 rounded-xl text-[14px] font-semibold text-slate-700 hover:bg-slate-100/70 transition-all"
                        >
                            Producto
                            <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform duration-200 ${mobileProductoOpen ? 'rotate-180' : ''}`} />
                        </button>

                        {/* Accordion content */}
                        <AnimatePresence initial={false}>
                            {mobileProductoOpen && (
                                <motion.div
                                    key="accordion"
                                    initial={{ height: 0, opacity: 0 }}
                                    animate={{ height: 'auto', opacity: 1 }}
                                    exit={{ height: 0, opacity: 0 }}
                                    transition={{ duration: 0.25, ease: EASE_OUT }}
                                    style={{ overflow: 'hidden' }}
                                >
                                    <div className="grid grid-cols-2 gap-x-3 px-2 pb-2 pt-1">
                                        {/* Soluciones */}
                                        <div>
                                            <p className="px-2 pb-1.5 text-[9px] font-mono tracking-[0.2em] text-slate-400 uppercase">Soluciones</p>
                                            {SOLUTIONS.map(sol => {
                                                const Icon = sol.icon;
                                                return (
                                                    <Link key={sol.slug} href={`/soluciones/${sol.slug}`} onClick={close}
                                                        className="flex items-center gap-2 py-1.5 px-2 rounded-lg text-[12px] font-medium text-slate-700 hover:bg-blue-50 transition-all">
                                                        <Icon className="w-3.5 h-3.5 text-blue-500 shrink-0" />
                                                        {sol.label}
                                                    </Link>
                                                );
                                            })}
                                        </div>
                                        {/* Por Sector */}
                                        <div>
                                            <p className="px-2 pb-1.5 text-[9px] font-mono tracking-[0.2em] text-slate-400 uppercase">Por Sector</p>
                                            {SECTORS.map(sec => {
                                                const Icon = sec.icon;
                                                return (
                                                    <Link key={sec.slug} href={`/sectores/${sec.slug}`} onClick={close}
                                                        className="flex items-center gap-2 py-1.5 px-2 rounded-lg text-[12px] font-medium text-slate-600 hover:bg-slate-100 transition-all">
                                                        <Icon className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                                                        {sec.label}
                                                    </Link>
                                                );
                                            })}
                                        </div>
                                    </div>
                                </motion.div>
                            )}
                        </AnimatePresence>

                        {/* Main nav links */}
                        <Link href="/software-ia" onClick={close} className="flex items-center px-3 py-3 rounded-xl text-[14px] font-medium text-slate-700 hover:bg-slate-100/70 transition-all">IA & Automatización</Link>
                        <Link href="/#proceso" onClick={close} className="flex items-center px-3 py-3 rounded-xl text-[14px] font-medium text-slate-700 hover:bg-slate-100/70 transition-all">Proceso</Link>
                        <Link href="/#nosotros" onClick={close} className="flex items-center px-3 py-3 rounded-xl text-[14px] font-medium text-slate-700 hover:bg-slate-100/70 transition-all">Sobre nosotros</Link>
                        <Link href="/#precios" onClick={close} className="flex items-center px-3 py-3 rounded-xl text-[14px] font-medium text-slate-700 hover:bg-slate-100/70 transition-all">Precios</Link>
                        <Link href="/contacto" onClick={close} className="flex items-center px-3 py-3 rounded-xl text-[14px] font-medium text-slate-700 hover:bg-slate-100/70 transition-all">Contacto</Link>

                        <div className="mt-2 pt-3 border-t border-slate-200/50 space-y-3">
                            {/* RRSS row — mobile */}
                            <div className="flex items-center justify-center gap-1">
                                {SOCIAL_LINKS.map(({ href, label, icon: Icon }) => (
                                    <SocialIcon key={label} href={href} label={label}>
                                        <Icon />
                                    </SocialIcon>
                                ))}
                            </div>
                            <Link
                                href="/#contacto"
                                onClick={close}
                                className="flex items-center justify-center gap-2 w-full py-3.5 rounded-xl bg-blue-600 text-white font-bold text-[15px] active:scale-95 transition-all hover:bg-blue-700 shadow-[0_4px_16px_rgba(37,99,235,0.3)]"
                            >
                                <Phone className="w-4 h-4" />
                                Quiero un presupuesto
                            </Link>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </nav>
    );
}
